import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import zIndex from "@/src/styles/zIndex";

interface ChatInitializationStatusProps {
  isLoading: boolean;
  error: string | null;
  retryCount: number;
  maxRetries: number;
  showChannel: boolean;
  onRetry?: () => void;
}

/**
 * 채팅 초기화 상태를 표시하는 컴포넌트
 * 로딩, 에러, 재시도 상태를 사용자에게 표시합니다.
 */
const ChatInitializationStatus: React.FC<ChatInitializationStatusProps> = ({
  isLoading,
  error,
  retryCount,
  maxRetries,
  showChannel,
  onRetry,
}) => {
  const { t } = useTranslation("chat");

  // 채널이 열려있으면 표시하지 않음
  if (showChannel) return null;

  return (
    <>
      {/* Error notification */}
      {error && retryCount >= maxRetries && (
        <ErrorNotification>
          <div className="title">
            {t("chatConnectionFailed", "채팅 연결 실패")}
          </div>
          <div className="message">{error}</div>
          {onRetry && (
            <RetryButton onClick={onRetry}>
              {t("retry", "다시 시도")}
            </RetryButton>
          )}
        </ErrorNotification>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <LoadingIndicator>
          <div className="message">
            {t("connectingToChat", "채팅 연결 중...")}
          </div>
          {retryCount > 0 && (
            <div className="retry-count">
              {t("retryAttempt", {
                current: retryCount,
                max: maxRetries,
                defaultValue: `재시도 ${retryCount}/${maxRetries}`,
              })}
            </div>
          )}
        </LoadingIndicator>
      )}
    </>
  );
};

const ErrorNotification = styled.div`
  position: fixed;
  bottom: 140px;
  right: 80px;
  background: white;
  border: 1px solid #ff4444;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: ${zIndex.floatingChat - 1};
  max-width: 280px;

  .title {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
  }

  .message {
    font-size: 12px;
    color: #666;
    margin-bottom: 12px;
  }
`;

const RetryButton = styled.button`
  padding: 6px 12px;
  background-color: #166dd7;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1557b0;
  }
`;

const LoadingIndicator = styled.div`
  position: fixed;
  bottom: 140px;
  right: 80px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: ${zIndex.floatingChat - 1};

  .message {
    font-size: 12px;
  }

  .retry-count {
    font-size: 11px;
    color: #666;
    margin-top: 4px;
  }
`;

export default ChatInitializationStatus;
